import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Mail, Calendar, Send, Users, UserCheck } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoReportSettings = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingSkytelerderTest, setSendingSkytelerderTest] = useState({});
  const [testEmail, setTestEmail] = useState('');
  const [testFrequency, setTestFrequency] = useState('monthly');
  const [recipients, setRecipients] = useState([]);
  const [skyteledere, setSkyteledere] = useState([]);
  const [skytelerderRecipients, setSkytelerderRecipients] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchSkyteledere();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-report-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data.recipients || []);
      setSkytelerderRecipients(response.data.skyteleder_recipients || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report settings:', error);
      toast.error('Kunne ikke laste rapportinnstillinger');
      setLoading(false);
    }
  };

  const fetchSkyteledere = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show skyteledere and admins
      const eligibleUsers = (response.data || []).filter(u => 
        u.approved && 
        (u.role === 'skyteleder' || u.role === 'admin' || u.role === 'org_admin')
      ).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb'));
      setSkyteledere(eligibleUsers);
    } catch (error) {
      console.error('Error fetching skyteledere:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/api/admin/ammo-report-settings`,
        { recipients, skyteleder_recipients: skytelerderRecipients },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rapportinnstillinger lagret!');
    } catch (error) {
      console.error('Error saving report settings:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke lagre innstillinger';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = () => {
    if (recipients.length >= 10) {
      toast.error('Maksimum 10 mottakere tillatt');
      return;
    }
    setRecipients([
      ...recipients,
      { email: '', frequency: 'monthly', enabled: true }
    ]);
  };

  const handleRemoveRecipient = (index) => {
    const updated = recipients.filter((_, i) => i !== index);
    setRecipients(updated);
  };

  const handleRecipientChange = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const handleSendTestReport = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Vennligst fyll inn en gyldig e-postadresse');
      return;
    }

    try {
      setSendingTest(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/admin/test-ammo-report`,
        { email: testEmail, frequency: testFrequency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test-rapport sendt til ${testEmail}`);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test report:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke sende test-rapport';
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleToggleSkytelelder = (userId, email) => {
    const exists = skytelerderRecipients.find(r => r.user_id === userId);
    if (exists) {
      // Remove
      setSkytelerderRecipients(skytelerderRecipients.filter(r => r.user_id !== userId));
    } else {
      // Add
      setSkytelerderRecipients([...skytelerderRecipients, { user_id: userId, email, enabled: true }]);
    }
  };

  const handleSendSkytelerderTestReport = async (skyteleder) => {
    try {
      setSendingSkytelerderTest(prev => ({ ...prev, [skyteleder.id]: true }));
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/admin/test-skyteleder-ammo-report`,
        { 
          email: skyteleder.email,
          skyteleder_id: skyteleder.id,
          skyteleder_name: skyteleder.full_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test-rapport sendt til ${skyteleder.email}`);
    } catch (error) {
      console.error('Error sending skyteleder test report:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke sende test-rapport';
      toast.error(errorMsg);
    } finally {
      setSendingSkytelerderTest(prev => ({ ...prev, [skyteleder.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-400">Laster innstillinger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Automatiske Rapporter</h3>
        <p className="text-sm text-gray-400">
          Konfigurer automatisk utsendelse av ammunisjonsrapporter via e-post.
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <p className="font-medium mb-2">Sendefrekvens:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-300 grid grid-cols-2 gap-2">
              <li><strong>Daglig:</strong> Hver dag kl. 08:00 (gårdagens salg)</li>
              <li><strong>Ukentlig:</strong> Mandag kl. 08:00 (siste 7 dager)</li>
              <li><strong>Månedlig:</strong> 1. i måneden kl. 08:00 (siste 30 dager)</li>
              <li><strong>Kvartalsvis:</strong> 1. jan/apr/jul/okt kl. 08:00 (siste 90 dager)</li>
              <li><strong>Halvårlig:</strong> 1. jan/jul kl. 08:00 (siste 180 dager)</li>
              <li><strong>Årlig:</strong> 1. januar kl. 08:00 (siste 365 dager)</li>
              <li><strong>Alt fra start:</strong> Månedlig, inkl. all historikk</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recipients List */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-white">
            E-postmottakere ({recipients.length}/10)
          </h4>
          <button
            onClick={handleAddRecipient}
            disabled={recipients.length >= 10}
            className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-md text-sm text-white transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Legg til</span>
          </button>
        </div>

        {recipients.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Ingen mottakere konfigurert ennå</p>
            <p className="text-sm mt-1">Klikk "Legg til" for å legge til en mottaker</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recipients.map((recipient, index) => (
              <div
                key={index}
                className="bg-gray-700 rounded-lg p-4"
              >
                {/* Top row: Email field - full width on mobile */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">E-postadresse</label>
                  <input
                    type="email"
                    value={recipient.email}
                    onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                    placeholder="eksempel@epost.no"
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Bottom row: Frequency, Toggle, Delete - wraps on mobile */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Frequency Dropdown */}
                  <div className="w-full sm:w-auto sm:min-w-[140px]">
                    <label className="block text-xs text-gray-400 mb-1">Frekvens</label>
                    <select
                      value={recipient.frequency}
                      onChange={(e) => handleRecipientChange(index, 'frequency', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Daglig</option>
                      <option value="weekly">Ukentlig</option>
                      <option value="monthly">Månedlig</option>
                      <option value="quarterly">Kvartalsvis</option>
                      <option value="semiannual">Halvårlig</option>
                      <option value="yearly">Årlig</option>
                      <option value="all_time">Alt fra start</option>
                    </select>
                  </div>

                  {/* Enabled Toggle */}
                  <div className="flex flex-col items-center">
                    <label className="text-xs text-gray-400 mb-2">Aktiv</label>
                    <button
                      onClick={() => handleRecipientChange(index, 'enabled', !recipient.enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        recipient.enabled ? 'bg-green-600' : 'bg-gray-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          recipient.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleRemoveRecipient(index)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-600/15 hover:bg-red-600/30 border border-red-600/40 hover:border-red-500 rounded-md transition-colors text-red-300 hover:text-white text-sm font-medium ml-auto"
                    title="Fjern mottaker"
                    data-testid={`remove-recipient-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Fjern</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors"
        >
          <Save className="w-5 h-5" />
          <span>{saving ? 'Lagrer...' : 'Lagre innstillinger'}</span>
        </button>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-6"></div>

      {/* Test Report Section */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-md font-medium text-white mb-4">Send test-rapport</h4>
        <p className="text-sm text-gray-400 mb-4">
          Send en test-rapport for å se hvordan e-posten ser ut før du aktiverer automatiske utsendelser.
        </p>
        <div className="space-y-3">
          {/* Email field - full width on mobile */}
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Din e-postadresse"
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {/* Frequency and Send button - wrap on mobile */}
          <div className="flex flex-wrap gap-3">
            <select
              value={testFrequency}
              onChange={(e) => setTestFrequency(e.target.value)}
              className="flex-1 min-w-[120px] px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daglig</option>
              <option value="weekly">Ukentlig</option>
              <option value="monthly">Månedlig</option>
              <option value="quarterly">Kvartalsvis</option>
              <option value="semiannual">Halvårlig</option>
              <option value="yearly">Årlig</option>
              <option value="all_time">Alt fra start</option>
            </select>
            <button
              onClick={handleSendTestReport}
              disabled={sendingTest || !testEmail}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
            >
              <Send className="w-5 h-5" />
              <span>{sendingTest ? 'Sender...' : 'Send test'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-6"></div>

      {/* Skyteleder Reports Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-yellow-500" />
            Daglige rapporter til Skyteledere
          </h3>
          <p className="text-sm text-gray-400">
            Velg hvilke Skyteledere og Admins som skal motta daglig e-postrapport for treninger de selv har vært Skyteleder på.
            Rapporten sendes automatisk dagen etter treningsøkten.
          </p>
        </div>

        {/* Info Box for Skyteleder Reports */}
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Users className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-200">
              <p className="font-medium mb-1">Hvordan dette fungerer:</p>
              <ul className="list-disc list-inside space-y-1 text-yellow-300">
                <li>Skyteleder mottar <strong>kun</strong> rapport for treninger de selv har ledet</li>
                <li>Rapporten sendes kl. 08:00 dagen etter treningsøkten</li>
                <li>Inneholder ammunisjonssalg kun fra den aktuelle treningsøkten</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Skyteledere List */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="text-md font-medium text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Skyteledere og Admins ({skyteledere.length})
          </h4>
          
          {skyteledere.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Ingen skyteledere funnet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {skyteledere.map((skyteleder) => {
                const isSelected = skytelerderRecipients.some(r => r.user_id === skyteleder.id);
                const isSending = sendingSkytelerderTest[skyteleder.id];
                
                return (
                  <div
                    key={skyteleder.id}
                    className={`p-3 rounded-lg transition-colors ${
                      isSelected ? 'bg-green-900/30 border border-green-700/50' : 'bg-gray-700'
                    }`}
                  >
                    {/* Top row: Checkbox, User info, Role badge */}
                    <div className="flex items-center gap-3 mb-2">
                      {/* Checkbox */}
                      <button
                        onClick={() => handleToggleSkytelelder(skyteleder.id, skyteleder.email)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected 
                            ? 'bg-green-600 border-green-600' 
                            : 'border-gray-500 hover:border-gray-400'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{skyteleder.full_name}</p>
                        <p className="text-gray-400 text-xs sm:text-sm truncate">{skyteleder.email}</p>
                      </div>
                      
                      {/* Role Badge */}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                        skyteleder.role === 'admin' || skyteleder.role === 'org_admin'
                          ? 'bg-purple-500/30 text-purple-300'
                          : 'bg-yellow-500/30 text-yellow-300'
                      }`}>
                        {skyteleder.role === 'admin' || skyteleder.role === 'org_admin' ? 'Admin' : ''}
                      </span>
                    </div>
                    
                    {/* Bottom row: Send Test Report Button + Remove (when selected) */}
                    <div className="flex justify-end items-center gap-2">
                      {isSelected && (
                        <button
                          onClick={() => handleToggleSkytelelder(skyteleder.id, skyteleder.email)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/15 hover:bg-red-600/30 border border-red-600/40 hover:border-red-500 rounded-md transition-colors text-red-300 hover:text-white text-xs sm:text-sm font-medium"
                          title="Fjern fra mottakerlisten"
                          data-testid={`remove-skyteleder-${skyteleder.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Fjern</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleSendSkytelerderTestReport(skyteleder)}
                        disabled={isSending || !skyteleder.email}
                        className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-white text-xs sm:text-sm font-medium transition-colors"
                      >
                        <Send className="w-3 h-3" />
                        <span className="hidden sm:inline">{isSending ? 'Sender...' : 'Send test-rapport'}</span>
                        <span className="sm:hidden">{isSending ? '...' : 'Test-rapport'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Save Button for Skyteleder Settings */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'Lagrer...' : 'Lagre skyteleder-innstillinger'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmmoReportSettings;
